import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import getCMResponse from '../../utils/api_manager';
import { getPostAuthRoute } from '../../utils/postAuthRoute';
import { buildProjectPath } from '../../utils/projectPath';
import styles from './AcceptOwnershipTransfer.module.css';

// Project Role-Enforcement Middleware TRD, Section 3.4. Structurally
// parallel to (not a modification of) AcceptInvite.jsx -- a distinct entity
// (user.pending_ownership_transfers, not pending_invitations) with different
// accept-time semantics (atomic OWNER downgrade, not a plain role grant)
// warrants its own component rather than overloading AcceptInvite.jsx with
// a type discriminator, matching this codebase's existing one-page-per-flow
// granularity (AcceptInvite.jsx itself is single-purpose).
//
// The invitee only reaches this page via the emailed acceptUrl link
// (?transferId=<id>) -- there is no carousel/multi-pending browsing mode
// here the way AcceptInvite.jsx has for plain invitations (TRD Section
// 3.4's route note), so this file only implements the "specific transfer"
// branch of that sibling file's logic.
//
// Expiry itself is never computed client-side -- the backend's own
// `is_expired` flag on each pending_ownership_transfers entry (TRD Section
// 2.4.5) and the 410 status on accept/reject (Section 2.4.3) are the two
// sources of truth this component reads, so no local 7-day constant is
// needed here (unlike AcceptInvite.jsx's INVITATION_EXPIRY_MS, which
// recomputes expiry client-side for its carousel filtering -- a mode this
// page doesn't have).

const AcceptOwnershipTransfer = ({ user, initUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);

  const transferId = searchParams.get('transferId');
  const isLoggedIn = user && Object.keys(user).length > 0;

  // LOGGED-OUT SCREEN
  if (!isLoggedIn) {
    return null;
  }

  if (!transferId) return null;

  const targetTransfer = (user?.pending_ownership_transfers || []).find(t => t.id === transferId);

  // INVALID TRANSFER SCREEN — not found / doesn't belong to this account /
  // already resolved. Distinct from the expired-but-still-PENDING case
  // below (TRD Section 3.4/2.4.3's deliberate 410-vs-404/generic distinction).
  if (!targetTransfer) {
    return (
      <div className={styles.pageBg}>
        <div className={styles.card}>
          <div className={styles.logoMark}>CQ</div>
          <h1 className={styles.title}>Invitation not found</h1>
          <p className={styles.subtitle}>
            This ownership-transfer invitation is invalid, has already been used, or doesn't belong to your account.
          </p>
          <button
            className={styles.primaryButton}
            onClick={() => {
              // No specific project is known here (targetTransfer wasn't
              // found) -- previously fell through to the bare /analyze/*
              // legacy-alias route's own projects[0] resolution, which is
              // now gone (confirmed bookmark-only dead weight, unrelated to
              // this reachable in-app button). getPostAuthRoute is the same
              // centralized decision initUser/the project switcher use,
              // so this reproduces that fallback directly instead of relying
              // on a route that no longer exists.
              const projects = user.projects || [];
              navigate(
                getPostAuthRoute(projects, user.pending_invitations || [], projects[0] || {}),
                { replace: true }
              );
            }}
          >
            Go to dashboards
          </button>
        </div>
      </div>
    );
  }

  // EXPIRED SCREEN — either the profile-fetched record already flags
  // is_expired, or a later accept/reject call comes back 410 (handled via
  // the `expired` state below). Same copy either way.
  if (expired || targetTransfer.is_expired) {
    return (
      <div className={styles.pageBg}>
        <div className={styles.card}>
          <div className={styles.logoMark}>CQ</div>
          <h1 className={styles.title}>This invitation has expired</h1>
          <p className={styles.subtitle}>
            Ask {targetTransfer.current_owner_name || 'the current owner'} to send a new ownership-transfer invitation.
          </p>
          <button
            className={styles.primaryButton}
            onClick={() => navigate(
              buildProjectPath(targetTransfer.project_id, '/analyze/dashboards/all'),
              { replace: true }
            )}
          >
            Go to dashboards
          </button>
        </div>
      </div>
    );
  }

  const handleAccept = () => {
    setLoading(true);
    setError(null);
    getCMResponse(`/project/ownership-transfer/${targetTransfer.id}/accept`, 'POST')
      .then(() => {
        initUser();
        // connect-cta-new-tab-project-leak-trd.md Section 3, R6: route
        // to the SPECIFIC project whose ownership was just accepted
        // (targetTransfer's own project_id), not the legacy-alias
        // fallback's projects[0].
        navigate(`/${targetTransfer.project_id}/analyze/dashboards/all`, { replace: true });
      })
      .catch((err) => {
        if (err.response?.status === 410) {
          setExpired(true);
        } else {
          setError('Failed to accept the ownership transfer. Please try again.');
        }
        setLoading(false);
      });
  };

  const handleReject = () => {
    setLoading(true);
    setError(null);
    getCMResponse(`/project/ownership-transfer/${targetTransfer.id}/reject`, 'POST')
      .then(() => {
        // Same reasoning as handleAccept above -- route to this specific
        // transfer's project_id, not the legacy-alias fallback's
        // projects[0].
        navigate(`/${targetTransfer.project_id}/analyze/dashboards/all`, { replace: true });
      })
      .catch((err) => {
        if (err.response?.status === 410) {
          setExpired(true);
        } else {
          setError('Failed to reject the ownership transfer. Please try again.');
        }
        setLoading(false);
      });
  };

  return (
    <div className={styles.pageBg}>
      <div className={styles.card}>
        <div className={styles.logoMark}>CQ</div>
        <h1 className={styles.title}>Ownership transfer requested</h1>
        <p className={styles.subtitle}>
          {targetTransfer.current_owner_name
            ? <><span className={styles.highlight}>{targetTransfer.current_owner_name}</span> wants to transfer ownership of</>
            : 'You have been offered ownership of'
          }
        </p>

        <div className={styles.projectBlock}>
          <span className={styles.projectName}>{targetTransfer.project_name || 'a project'}</span>
        </div>

        <div className={styles.roleBlock}>
          <span className={styles.roleLabel}>Your new role</span>
          <span className={styles.roleBadge}>Owner</span>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}

        <div className={styles.ctaRow}>
          <button
            className={styles.rejectButton}
            onClick={handleReject}
            disabled={loading}
          >
            Reject
          </button>
          <button
            className={styles.acceptButton}
            onClick={handleAccept}
            disabled={loading}
          >
            {loading ? 'Processing…' : 'Accept transfer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptOwnershipTransfer;
